/* eslint-disable */
// @ts-nocheck

class DiscountUpdateEvent extends CustomEvent {
  constructor(data, id) {
    super('discount:update', {
      detail: {data, id},
      bubbles: true,
    });
  }
}

function fetchConfig(type = 'json', options = {}) {
  return {
    method: 'POST',
    headers: {
      'Content-Type':
        type === 'json'
          ? 'application/json'
          : 'application/x-www-form-urlencoded',
      Accept: `application/${type}`,
    },
    ...options,
  };
}

function morphSection(cartSummary, newHtml) {
  const sectionEl = document.getElementById(cartSummary);
  const newDoc = new DOMParser().parseFromString(newHtml, 'text/html');
  const newSection = newDoc.getElementById(cartSummary);

  if (sectionEl && newSection) {
    sectionEl.replaceWith(newSection);
  }
}

// Morph a target using a CSS selector instead of an element id.
// Useful to update partial content (e.g., cart drawer summary) without
// replacing the entire drawer element, which would close it.
function morphTarget(selector, newHtml) {
  const targetEl = document.querySelector(selector);
  const newDoc = new DOMParser().parseFromString(newHtml, 'text/html');
  const newTarget = newDoc.querySelector(selector);

  if (targetEl && newTarget) {
    targetEl.replaceWith(newTarget);
  }
}

function cartPerformanceMark(name) {
  try {
    performance.mark(name);
  } catch (err) {
    console.debug('Performance marks not supported in this environment.', err);
  }
}

/**
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError
 * @property {HTMLElement} cartDiscountErrorDiscountCode
 * @property {HTMLElement} cartDiscountErrorShipping
 */

class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.applyDiscount = this.applyDiscount.bind(this);
    this.removeDiscount = this.removeDiscount.bind(this);
    this.cartDrawer = document.querySelector('cart-drawer');
    this.mainCart = document.querySelector('.cc-main-cart');
  }

  connectedCallback() {
    this.refs = {
      cartDiscountError: this.querySelector('[ref="cartDiscountError"]'),
      cartDiscountErrorDiscountCode: this.querySelector(
        '[ref="cartDiscountErrorDiscountCode"]',
      ),
      cartDiscountErrorShipping: this.querySelector(
        '[ref="cartDiscountErrorShipping"]',
      ),
      cartDiscountErrorInvalid: this.querySelector(
        '[ref="cartDiscountErrorInvalid"]',
      ),
    };

    // Attach submit listener if a form exists (non-essential in our JS-driven UI)
    const formEl = this.querySelector('form');
    this.addEventListener('submit', this.applyDiscount);

    // Use delegated listeners so they survive DOM morphs
    this.addEventListener('click', (e) => {
      if (!(e.target instanceof HTMLElement)) return;
      if (e.target.closest('.cart-discount__button')) {
        this.applyDiscount(e);
      }
    });
    this.addEventListener('keydown', (e) => {
      if (!(e.target instanceof HTMLElement)) return;
      if (e.key === 'Enter' && e.target.closest('input[name="discount"]')) {
        this.applyDiscount(e);
      }
    });
    this.addEventListener('click', this.removeDiscount);
    this.addEventListener('keydown', this.removeDiscount);

    // Live sanitization of discount code input
    const inputEl = this.querySelector('input[name="discount"]');
    if (inputEl) {
      inputEl.addEventListener('input', () => {
        const sanitized = this.#sanitizeCode(inputEl.value || '');
        if (inputEl.value !== sanitized) inputEl.value = sanitized;
        // Clear any prior invalid state while the user edits
        try { inputEl.setCustomValidity(''); } catch (err) {
          console.warn('Could not clear discount input validity state.', err);
        }
        inputEl.removeAttribute('aria-invalid');
        this.refs.cartDiscountError?.classList.add('hidden');
        this.refs.cartDiscountErrorInvalid?.classList.add('hidden');
        this.refs.cartDiscountErrorDiscountCode?.classList.add('hidden');
        this.refs.cartDiscountErrorShipping?.classList.add('hidden');
      });
      inputEl.addEventListener('blur', () => {
        const sanitized = this.#sanitizeCode(inputEl.value || '');
        if (inputEl.value !== sanitized) inputEl.value = sanitized;
      });
    }

    // Ensure discount persists to checkout: intercept cart form submits
    // and redirect to /checkout?discount=CODE when a code is present.
    // Scoped to the cart forms, cleaner than document-wide click.
    this._checkoutSubmitHandler = (e) => {
      const target = e.target;
      if (!(target instanceof HTMLFormElement)) return;
      const formId = target.id || '';
      const action = (target.getAttribute('action') || '').toLowerCase();
      const isCartForm = formId === 'cart' || formId === 'cart-drawer-form' || action.endsWith('/cart');
      if (!isCartForm) return;

      // Respect cart terms checkbox if present (only blocks on main cart form)
      if (formId === 'cart') {
        const termsCheck = document.getElementById('cart-terms');
        if (termsCheck && !termsCheck.checked) return; // let native validation/handlers run
      }

      const codes = this.#existingDiscounts();
      // Only pass an applied code (from pills). If you want to allow a typed-but-not-applied code, uncomment the next lines
      // const inputEl = target.querySelector('input[name="discount"]') || this.querySelector('input[name="discount"]');
      // const inputCode = inputEl && inputEl.value ? inputEl.value.trim() : '';
      const discountCodeValue = codes[codes.length - 1];
      if (!discountCodeValue) return; // no applied code, proceed with native checkout

      e.preventDefault();
      const shopifyRoot = window?.Shopify?.routes?.root || '/';
      const url = `${shopifyRoot}checkout?discount=${encodeURIComponent(discountCodeValue)}`;
      try { window.location.href = url; } catch (err) {
        console.error('Failed to redirect to checkout with discount.', err);
      }
    };
    // Bind once globally to avoid duplicates when multiple discount components exist
    if (!window.__bf_discount_submit_bound) {
      window.__bf_discount_submit_bound = true;
      document.addEventListener('submit', this._checkoutSubmitHandler, true);
    }
  }

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) this.#activeFetch.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;
    return controller;
  }

    async applyDiscount(event) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
      cartDiscountErrorInvalid,
    } = this.refs;

    // Resolve the form element robustly (submit target may vary by browser)
    const formEl =
      event.target instanceof HTMLFormElement
        ? event.target
        : (event.target && typeof event.target.closest === 'function'
            ? event.target.closest('form')
            : null) || this.querySelector('form');

    if (event && typeof event.preventDefault === 'function') event.preventDefault();
    if (event && typeof event.stopPropagation === 'function') event.stopPropagation();

    const input = (formEl
      ? formEl.querySelector('input[name="discount"]')
      : this.querySelector('input[name="discount"]'));

    const drawerSectionId = this.cartDrawer?.id;
    const mainCartSectionId = this.mainCart?.id;

    // Proceed if input exists; do not require drawer presence (supports main cart page too)
    if (!(input instanceof HTMLInputElement)) return;

    let discountCodeValue = this.#sanitizeCode(input.value || '');
    input.value = discountCodeValue; // keep UI consistent
    if (!discountCodeValue) return;

    // Validate format and length before making network calls
    const isValid = this.#isValidCode(discountCodeValue);
    if (!isValid) {
      cartDiscountError.classList.remove('hidden');
      cartDiscountErrorInvalid?.classList.remove('hidden');
      try { input.setCustomValidity('Invalid code'); } catch (err) {
        console.warn('Could not set discount input to invalid state.', err);
      }
      input.setAttribute('aria-invalid', 'true');
      return;
    }

    const existing = this.#existingDiscounts();
    if (existing.includes(discountCodeValue)) return;

    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    cartDiscountErrorInvalid?.classList.add('hidden');

    const abortController = this.#createAbortController();
    const config = fetchConfig('json', {
      body: JSON.stringify({
        discount: [...existing, discountCodeValue].join(','),
        sections: this.getSectionsToRender().map((section) => section.section),
      }),
      signal: abortController.signal,
    });

    try {
      const shopifyRoot = window?.Shopify?.routes?.root || '/';
      const cartUpdateUrl = `${shopifyRoot}cart/update.js`;
      const res = await fetch(cartUpdateUrl, config);
      const data = await res.json();
      const notApplicable = data.discount_codes.find(
        (d) => d.code === discountCodeValue && !d.applicable,
      );
      if (notApplicable) {
        input.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }
      if (this.cartDrawer) {
        // Refresh only the cart drawer summary to avoid closing the drawer
        this.updateCartSummary(
          'cart-drawer',
          '.cart-drawer__summary',
          drawerSectionId,
          data,
          discountCodeValue,
          existing,
        );
      }
      if (this.mainCart) {
        // Refresh the main cart section and morph the discount container in-place
        this.updateCartSummary(
          'main-cart',
          'cart-discount-container',
          mainCartSectionId,
          data,
          discountCodeValue,
          existing,
        );
      }

      // Prefer using cart-items web component for a unified refresh of items and subtotals
      const cartItemsEl = document.querySelector('cart-items');
      if (cartItemsEl && typeof cartItemsEl.refresh === 'function') {
        try { await cartItemsEl.refresh(); } catch (err) {
          console.warn('Cart items failed to refresh after applying discount.', err);
        }
      }
    } catch (err) {
      console.error('Failed to apply discount.', err);
    } finally {
      this.#activeFetch = null;
      cartPerformanceMark('discount-update:user-action');
    }
  }

  async removeDiscount(event) {
    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent || event instanceof KeyboardEvent) ||
      !(event.target instanceof HTMLElement)
    )
      return;

    const pill = event.target.closest('.cart-discount__pill');
    const drawerSectionId = this.cartDrawer?.id;
    const mainCartSectionId = this.mainCart?.id;

    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode || !this.dataset.sectionId) return;

    const existing = this.#existingDiscounts();
    const idx = existing.indexOf(discountCode);
    if (idx === -1) return;
    existing.splice(idx, 1);

    const abortController = this.#createAbortController();
    const config = fetchConfig('json', {
      body: JSON.stringify({
        discount: existing.join(','),
        sections: this.getSectionsToRender().map((section) => section.section),
      }),
      signal: abortController.signal,
    });

    try {
      const shopifyRoot = window?.Shopify?.routes?.root || '/';
      const cartUpdateUrl = `${shopifyRoot}cart/update.js`;
      const res = await fetch(cartUpdateUrl, config);
      const data = await res.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));

      const newHtml = data.sections['cart-drawer'];
      if (newHtml) {
        // Refresh only the cart drawer summary to avoid closing the drawer
        morphTarget('.cart-drawer__summary', newHtml);
      }

      const mainCartHtml = data.sections['main-cart'];
      if (mainCartHtml) {
        // Update the discount container on the main cart page so pills reflect removal
        morphSection('cart-discount-container', mainCartHtml);
      }

      // Immediately remove the pill from the live DOM so the UI reflects
      // the change without requiring a full page reload.
      if (pill && pill.parentElement) {
        const ul = pill.parentElement;
        try { pill.remove(); } catch (err) {
          console.warn('Failed to remove discount pill element.', err);
        }
        const remaining = ul.querySelectorAll('.cart-discount__pill').length;
        ul.classList.toggle('has-codes', remaining > 0);
      }

      // Prefer using cart-items web component for a unified refresh of items and subtotals
      const cartItemsEl = document.querySelector('cart-items');
      if (cartItemsEl && typeof cartItemsEl.refresh === 'function') {
        try { await cartItemsEl.refresh(); } catch (err) {
          console.warn('Cart items failed to refresh after removing discount.', err);
        }
      }
    } catch (err) {
      console.error('Failed to update discounts.', err);
    } finally {
      this.#activeFetch = null;
    }
  }

  #handleDiscountError(type) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
      cartDiscountErrorInvalid,
    } = this.refs;
    const target =
      type === 'discount_code'
        ? cartDiscountErrorDiscountCode
        : type === 'shipping'
        ? cartDiscountErrorShipping
        : cartDiscountErrorInvalid;

    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  #existingDiscounts() {
    return Array.from(this.querySelectorAll('.cart-discount__pill'))
      .filter((el) => el instanceof HTMLLIElement && el.dataset.discountCode)
      .map((el) => el.dataset.discountCode);
  }
  #sanitizeCode(code) {
    try {
      return (code || '')
        .toString()
        .replace(/\s+/g, '') // remove whitespace
        .replace(/[^A-Za-z0-9-]/g, '') // keep only letters, numbers, hyphen
        .toUpperCase()
        .trim();
    } catch (_) {
      return '';
    }
  }
  #isValidCode(code) {
    if (!code) return false;
    if (code.length < 3 || code.length > 32) return false;
    return /^[A-Z0-9-]{3,32}$/.test(code);
  }
  updateCartSummary(
    cartSection,
    cartSummary,
    sectionId,
    data,
    discountCodeValue,
    existing,
  ) {
    const newHtml = data.sections[cartSection];
    const tempDoc = new DOMParser().parseFromString(newHtml, 'text/html');
    const section = cartSummary.startsWith('.')
      ? tempDoc.querySelector(cartSummary)
      : tempDoc.getElementById(cartSummary);
    const codes = Array.from(
      section?.querySelectorAll('.cart-discount__pill') || [],
    )
      .map((el) => el.dataset.discountCode)
      .filter(Boolean);

    // Only infer shipping-only discount when the returned section actually
    // contains the discount pills container; otherwise we may falsely flag
    // shipping errors if this section doesn't include the pills.
    const pillsContainerPresent = !!section?.querySelector('.cart-discount__codes');

    // The new discount code was accepted by Shopify (d.applicable) but the
    // list of active codes in the returned HTML is identical to the list we
    // already had.  This means the code is valid but does not apply to the
    // current cart (typically a shipping-only code when the cart already has
    // free shipping).  Treat this situation as a “shipping” error so we can
    // show the user an appropriate message and clear the input.
    const isShippingError =
      pillsContainerPresent &&
      codes.length === existing.length &&
      codes.every((code) => existing.includes(code)) &&
      data.discount_codes.some(
        (d) => d.code === discountCodeValue && d.applicable,
      );
    if (isShippingError) {
      const inputEl = this.querySelector('input[name="discount"]');
      if (inputEl && inputEl.value !== undefined) {
        try { inputEl.value = ''; } catch (err) {
          console.warn('Could not clear discount input value.', err);
        }
      }
      this.#handleDiscountError('shipping');
      return;
    }

    document.dispatchEvent(new DiscountUpdateEvent(data, this.id));

    if (cartSummary.startsWith('.')) {
      morphTarget(cartSummary, newHtml);
    } else {
      morphSection(cartSummary, newHtml);
    }
  }
  getSectionsToRender() {
    /**
     * Return the list of actual Shopify section handles we want the
     * /cart/update.js endpoint to re-render. Snippets like
     * `cart-discount-container` are not valid section handles.
     */
    const sections = [
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '#cart-icon-bubble' },
      { id: 'free-shipping-notice', section: 'free-shipping-notice', selector: '#free-shipping-notice' },
    ];

    if (this.cartDrawer) {
      sections.push({ id: 'cart-drawer', section: 'cart-drawer', selector: '#cart-drawer' });
    }

    if (this.mainCart) {
      sections.push({ id: 'main-cart', section: 'main-cart', selector: '.cc-main-cart' });
    }

    return sections;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
